import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@apollo/client";
import { fetchCampaignsAction } from "../slices/campaignListSlice";
import { ON_COMPANY_DATA_IMPORT_JOB_IN_PROGRESS_COUNT_SUBSCRIPTION } from "../graphql/subscriptions/onCompanyDataImportJobInProgressCount";
import { RootState } from "@/app/rootReducer";
import { FetchCompanyCampaignsRequest } from "@/api/fetchCompanyCampaigns/types";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import { CampaignStatus } from "../api/fetchCampaignStatuses/types";
import { fetchCampaignStatuses } from "../api/fetchCampaignStatuses/fetchCampaignStatusesApi";
import { createCampaignsColumns } from "../components/CampaignColumns/CampaignColumns";

export function useCampaignList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaigns, totalCount, loading, error } = useSelector(
    (state: RootState) => state.campaignList,
  );
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );

  const companyId = userAppSettings?.companyId ?? 0;
  const { data: importData } = useSubscription(
    ON_COMPANY_DATA_IMPORT_JOB_IN_PROGRESS_COUNT_SUBSCRIPTION,
    {
      variables: { companyId },
      skip: !companyId,
    },
  );

  const inProgressCount =
    importData?.company_data_import_job_aggregate?.aggregate?.count ?? 0;
  const isImportInProgress = inProgressCount > 0;

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<{ name?: string }>({});
  const [campaignStatusId, setCampaignStatusId] = useState<number | undefined>(
    1,
  );
  const [campaignStatuses, setCampaignStatuses] = useState<CampaignStatus[]>(
    [],
  );

  const requestData = useMemo<FetchCompanyCampaignsRequest>(() => {
    return {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : "ASC",
      campaignStatusId,
    };
  }, [pagination, sorting, campaignStatusId]);

  useEffect(() => {
    dispatch(fetchCampaignsAction(requestData));
  }, [dispatch, requestData]);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const response = await fetchCampaignStatuses();
        setCampaignStatuses(response.data);
      } catch {
        // surfaced by the axios interceptor toast
      }
    };
    loadStatuses();
  }, []);

  const handleCreateCampaign = useCallback(() => {
    navigate("/lead-generation/campaigns/new");
  }, [navigate]);

  const handleViewBatches = useCallback(
    (id: number) => {
      navigate(`/lead-generation/campaigns/${id}/batches`);
    },
    [navigate],
  );

  const handleViewCampaign = useCallback(
    (id: number) => {
      navigate(`/lead-generation/campaigns/${id}/view`);
    },
    [navigate],
  );

  const handleEditCampaign = useCallback(
    (id: number) => {
      navigate(`/lead-generation/campaigns/${id}/edit`);
    },
    [navigate],
  );

  const handleCloneCampaign = useCallback(
    (id: number) => {
      navigate(`/lead-generation/campaigns/${id}/clone`);
    },
    [navigate],
  );

  const columns = useMemo(
    () =>
      createCampaignsColumns({
        handleViewBatches,
        handleViewCampaign,
        handleEditCampaign,
        handleCloneCampaign,
      }),
    [
      handleViewBatches,
      handleViewCampaign,
      handleEditCampaign,
      handleCloneCampaign,
    ],
  );

  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      setPagination(updaterOrValue);
    },
    [],
  );

  const handlePageChange = useCallback(
    (pageIndex: number, pageSize: number) => {
      setPagination({ pageIndex, pageSize });
    },
    [],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSorting(updaterOrValue);
    },
    [],
  );

  const handleFilterChange = useCallback((name: string) => {
    setFilters({ name });
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleCampaignStatusChange = useCallback((newStatusId?: number) => {
    setCampaignStatusId(newStatusId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const refetchCampaigns = () => {
    dispatch(fetchCampaignsAction(requestData));
  };

  return {
    campaigns,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    columns,
    campaignStatuses,
    isImportInProgress,
    handleCreateCampaign,
    handlePaginationChange,
    handlePageChange,
    handleSortingChange,
    handleFilterChange,
    handleCampaignStatusChange,
    campaignStatusId,
    refetchCampaigns,
  };
}
